import {
  Color,
  GsStructBorderRadius,
  GsStructBorderStyle,
  GsStructBorderWidth,
  IMethod,
  IPaginated,
  IReport,
  IReportBodySettings,
  ISimpleSearchFilterItem,
  IUiButtonSettings,
  ResponsiveAttribute
} from '@gs/shared';
import { GsStructComponent, GsStructPageItems, GsStructTextAlignment } from '../component';

/**
 * Tile wizard leading through the plant protection report categories to a symptom
 * (plant type » plant » symptom » report). The step is held in the URL (`?pd=`, `?pdr=`).
 */
export interface GsStructPlantDoctorComponent extends GsStructComponent<IPlantDoctorRender>, GsStructPageItems, IReportBodySettings {
  /** The specified type-key for this struct */
  componentType: 'PlantDoctor';
  /** Category the wizard starts at; empty starts at the top level */
  rootCategoryID?: string | number;
  /** Category IDs of the current path, filled from the `pd` query parameter */
  pathes?: string;
  /** ID of the selected report, filled from the `pdr` query parameter */
  reportID?: string | number;
  /** Height of the tile images (responsive) */
  itemImageHeight?: ResponsiveAttribute<number>;
  /** Crop the tile images to a 1:1 square instead of using {@link itemImageHeight} */
  forceSquareImage?: boolean;
  /** Border radius for the tile images */
  borderRadiusItemImage?: GsStructBorderRadius;
  /** Border width for the tile images */
  borderWidthItemImage?: GsStructBorderWidth;
  /** Border style for the tile images */
  borderStyleItemImage?: GsStructBorderStyle;
  /** Border color for the tile images */
  borderColorItemImage?: Color;
  /** Text alignment inside the tiles */
  itemTextAlignment?: GsStructTextAlignment;
  /** Zoom the tile image on hover */
  itemZoomOnHover?: boolean;
  /** Background color of the active step in the step bar */
  stepActiveBackgroundColor?: Color;
  /** Text color of the active step in the step bar */
  stepActiveTextColor?: Color;
  /** Configurable button styling (used for the "to the report" action) */
  buttons?: IUiButtonSettings;
}

export interface IPlantDoctorRender {
  /** The categories of the current path, from the top level down to the selection */
  steps: ISimpleSearchFilterItem[];
  /** The categories to choose from in the current step */
  categories: ISimpleSearchFilterItem[];
  /** The reports (symptoms) of the current category */
  items: IPaginated<ISimpleSearchFilterItem>;
  /** The selected report, only set in the last step */
  report?: IReport;
  /** Link to the report's own detail page, only set in the last step */
  reportLink?: string;
  /** The methods of the component */
  methods: IPlantDoctorMethods;
}

export interface IPlantDoctorMethods {
  /** Loads the state of a step (tile click, browser navigation, paging) */
  populate: IMethod;
}

/** The step the wizard is currently in — decides the heading above the tiles */
export enum PlantDoctorState {
  /** Top level: only plant types to choose from */
  PlantTypes = 'plantTypes',
  /** Plants and symptoms to choose from */
  PlantsAndSymptoms = 'plantsAndSymptoms',
  /** Only plants to choose from */
  Plants = 'plants',
  /** Only symptoms to choose from */
  Symptoms = 'symptoms',
  /** A report is selected */
  Report = 'report'
}
